import { OverlayRef } from '@angular/cdk/overlay';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { SessionTimerEventContext, SessionTimerService } from '@psei/ng';
import { DialogService } from '@psei/ng/dialog';
import jwtDecode from 'jwt-decode';
import { get } from 'lodash';
import { PpbToasterService } from 'projects/ppb-shared-library/src/lib/pseic-toaster/ppb-toaster.service';
import { FormChangeService } from 'projects/ppb-shared-library/src/lib/services/form-change.service';
import { LocalStorageService } from 'projects/ppb-shared-library/src/lib/services/local-storage.service';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, concatMap, switchMap, tap } from 'rxjs/operators';
import { AccessToken, AuthCredentialsResponse } from '../../../shared/models/auth-token.model';
import { LoginConfirmationResponseModel, SessionModel } from '../../../shared/models/login-confirmation-response.model';
import { SduExtRefreshResponse } from '../../../shared/models/sduext/sduext-refresh-repsonse-model';
import { SduExtUmaResponse } from '../../../shared/models/sduext/sduext-uma-response-model';
import { SduExtUmaToken } from '../../../shared/models/sduext/sduext-uma-token';
import { ServerConfigModel } from '../../../technical/version/server-config.model';
import { ApiMessage } from '../models/global.model';
import { UserElementalRole } from '../models/user-elemental-role';
import { AuthTokenService } from './auth-token.service';
import { PpbApiService } from './ppb-api.service';

@Injectable({
  providedIn: 'root'
})
export class UserService implements OnDestroy {
  userRoles: UserElementalRole[];
  overlayRef: OverlayRef;
  multipleSessions$ = new BehaviorSubject<SessionModel[]>(null);
  private subs = new Subscription();
  private authCredentials = new BehaviorSubject<AccessToken>(null);
  // tslint:disable-next-line:member-ordering
  authCredentials$ = this.authCredentials.asObservable();
  private _keyCloakURI: string;
  private loginConfirmationToken: string;
  private sessionSubs = new Subscription();

  constructor(
    private api: PpbApiService,
    private authTokenService: AuthTokenService,
    private router: Router,
    private dialogBoxService: DialogService,
    private sessionTimerService: SessionTimerService,
    private formChangeService: FormChangeService,
    private ppbToasterService: PpbToasterService,
    private localStorageService: LocalStorageService,
  ) {
    this.authCredentials.next(this.authTokenService.getTokenData());
    this.subs.add(this.sessionTimerService.extendSessionTimer$.subscribe(() => {
      this.refreshToken().subscribe((resp) => {
          this.setSessionTimer(resp.expires_in);
        },
        _ => {
          this.logOut().subscribe(_ => this.router.navigateByUrl('/login'));
        }
      );
    }));
  }

  setupSessionSubs(): void {
    this.sessionSubs.unsubscribe();
    this.sessionSubs = new Subscription();

    this.sessionSubs.add(
      this.sessionTimerService.endOfSessionTimer$.subscribe((context: SessionTimerEventContext) => {
        if (context === SessionTimerEventContext.Timeout) {
          this.overlayRef = this.ppbToasterService.open(
            $localize`:@@common.session_expired: Upłynął czas sesji. Wymagane jest ponowne zalogowanie do PPB.`, 'warning', false);
        }
        this.formChangeService.clearIsDirty();

        this.stopSession();
        this.router.navigateByUrl('/login')
      }));
  }

  get keyCloakURI(): string {
    return this._keyCloakURI;
  }

  set keyCloakURI(uri: string) {
    this._keyCloakURI = uri;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  hasRole(role: UserElementalRole): boolean {
    return this.userRoles.includes(role);
  }

  passwordChangeEmailSend(email: string): Observable<{ message: any[] }> {
    const body = { email };
    return this.api.put<{ message: any[] }>(
      `/auth-api/v1/public/accounts/link/reset/email`,
      body
    );
  }

  passwordChangeEmailTokenValidate(token: string): Observable<{ message: any[], token: string }> {
    const params = new HttpParams({
      fromObject: {
        token
      }
    });
    return this.api.get<{ message: any[], token: string }>(
      `/auth-api/v1/public/accounts/link/reset/email`,
      params,
      new HttpHeaders()
    );
  }

  passwordChangeFirstStep(password: string, token: string): Observable<{ message: any[], passwordStrength: number }> {
    const body = { password, token };
    return this.api.put<{ message: any[], passwordStrength: number }>(
      `/auth-api/v1/public/accounts/link/reset/sms`,
      body
    )
  }

  passwordChangeFinalStep(password: string, token: string, smsCode: number): Observable<{ message: any[] }> {
    const body = { password, token, smsCode };
    return this.api.put<{ message: any[] }>(
      `/auth-api/v1/public/accounts/link/reset/pwd`,
      body
    )
  }

  loginStep1(username: string, password: string): Observable<{ uuid: string }> {
    const body = new HttpParams({
      fromObject: {
        username,
        password,
        grant_type: 'password',
        client_id: 'frontend-ppb'
      },
      encoder: {
        encodeKey: (key: string) => encodeURIComponent(key),
        encodeValue: (key: string) => encodeURIComponent(key),
        decodeKey: (key: string) => encodeURIComponent(key),
        decodeValue: (key: string) => encodeURIComponent(key),
      }
    });

    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    return this.api.postPublic<{ uuid: string }>(
      '/auth-api/v1/public/auth/sms',
      body.toString(),
      new HttpParams(),
      headers
    )
  }

  loginStep2(uuid: string, username: string, smsCode: string):
    Observable<SduExtUmaResponse> {
    const body = new HttpParams({
      fromObject: {
        username,
        auth_code: smsCode,
        grant_type: 'password'
      }
    });
    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    return this.api.postPublic<AuthCredentialsResponse>(
      `/auth-api/v1/public/auth/sms/${uuid}`,
      body.toString(),
      new HttpParams(),
      headers
    ).pipe(
      tap(this.storeCredentials),
      concatMap(_ => this.confirmLogin().pipe(tap((resp) => {
        this.multipleSessions$.next(resp.userSessions);
      }))),
      concatMap(_ => this.changeAccountLanguage(this.localStorageService.getPreferredLanguage())),
      concatMap(this.fetchUserRoles),
    )
  }

  confirmLogin: () => Observable<LoginConfirmationResponseModel> = () => {
    return this.api.post<LoginConfirmationResponseModel>(
      `/api/v1/accounts/${this.authTokenService.getUserId()}/login-confirmation/${this.loginConfirmationToken}`
    )
  };

  changeAccountLanguage: (lang: string) => Observable<ApiMessage[]> = (lang) => {
    return this.api.post<ApiMessage[]>(
      `/api/v1/accounts/${this.authTokenService.getUserId()}/language`,
      { languageCode: lang }
    )
  };

  inviteUser(invitedUserLogin: string): Observable<null> {
    return this.api.post('/api/v1/invitations', {
      abpbLogin: this.authTokenService.getUserEmail(),
      invitedUserLogin
    })
  }

  storeCredentials = (authCredentials: AuthCredentialsResponse) => {
    this.loginConfirmationToken = authCredentials.loginConfirmationToken;
    this.authTokenService.storeCredentials(authCredentials);
    this.authCredentials.next(this.authTokenService.getTokenData());
  };

  fetchUserRoles: () => Observable<SduExtUmaResponse> = () => {
    const body = new HttpParams()
      .set('grant_type', 'urn:ietf:params:oauth:grant-type:uma-ticket')
      .set('client_id', 'frontend-ppb')
      .set('audience', 'ppb');

    const headers = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded');

    return this.api.post<SduExtUmaResponse>(
      `${this.keyCloakURI}/token`,
      body,
      new HttpParams(),
      headers
    ).pipe(
      tap((resp: SduExtUmaResponse) => {
        const seconds = resp.expires_in;
        this.sessionTimerService.initialize({
          localStorageKey: 'ppbSessionTimerKey',
          warningTime: 120 * 1000,
          sessionDuration: seconds * 1000,
        }).then(() => {
          this.setSessionTimer(seconds);
          this.setupSessionSubs();
          if (this.overlayRef) {
            this.overlayRef.detach();
            this.overlayRef = null;
          }
        })
        this.setRoles(resp);
      }),
    )
  };


  logOut = (): Observable<boolean> => {
    this.sessionSubs.unsubscribe();
    if (this.authTokenService.getAccessToken()) {
      return this.invalidateToken()
        .pipe(
          catchError(_ => {
            this.stopSession();
            return of(true)
          }),
          concatMap(_ => {
            this.dialogBoxService.legacy.closeAll();
            this.stopSession();
            return of(true)
          })
        )
    }
    this.stopSession();
    return of(true);
  };

  decodeToken<T>(token: string): T {
    return jwtDecode(token);
  }

  // tslint:disable-next-line:max-line-length
  handleLoadToken = (combined: { accessToken: AccessToken, keycloakURI: ServerConfigModel }): Observable<boolean | SduExtUmaResponse> => {
    if (combined.accessToken) {
      return this.refreshToken()
        .pipe(
          switchMap(this.fetchUserRoles),
        )
    } else {
      return of(null);
    }
  };

  refreshToken = (): Observable<SduExtRefreshResponse> => {
    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('client_id', 'frontend-ppb')
      .set('refresh_token', this.authTokenService.getRefreshToken());

    const headers = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded');
    return this.api.postPublic<SduExtRefreshResponse>(
      `${this.keyCloakURI}/token`,
      body,
      new HttpParams(),
      headers
    ).pipe(
      tap((resp: SduExtRefreshResponse) => {
        // TODO: Don't save bearer plz.
        this.authTokenService.storeCredentials({
          accessToken: resp.access_token,
          refreshToken: resp.refresh_token,
          scope: resp.scope,
          loginConfirmationToken: null
        });
        this.authCredentials.next(this.authTokenService.getTokenData());
      }),
    )
  };

  private setSessionTimer(expiresIn: number): void {
    const milliseconds = expiresIn * 1000;
    this.sessionTimerService.setSessionTimer(Date.now() + milliseconds);
  }

  private invalidateToken = (): Observable<any> => {
    const body = new HttpParams()
      .set('client_id', 'frontend-ppb')
      .set('refresh_token', this.authTokenService.getRefreshToken());

    const headers = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded');

    return this.api.postPublic<SduExtRefreshResponse>(
      `${this.keyCloakURI}/logout`,
      body,
      new HttpParams(),
      headers
    );
  };

  private stopSession(): void {
    this.authTokenService.stopSession();
    this.authCredentials.next(null)
  };

  private setRoles(resp: SduExtUmaResponse): void {
    const token = this.decodeToken(resp.access_token) as SduExtUmaToken;
    this.userRoles = this.extractRoles(token);
  }

  private extractRoles(token: SduExtUmaToken): UserElementalRole[] {
    return get(token as SduExtUmaToken, ['authorization', 'permissions'], [] as UserElementalRole[])
      // @ts-ignore
      .flatMap((permissions: { scopes: UserElementalRole[]; }) => permissions.scopes)
      .filter(Boolean);
  }
}
